import type { RawMatch } from "./bullionRawMatch";

/**
 * Scheduled wall-clock kickoff for Bullion (toss window, lobby cards, early-offer).
 * Does not use `actual_start` — that can move during toss feed updates.
 *
 * Keep field order aligned with `backend/utils/bullionFixtureScheduledStart.js`.
 */

type ScheduledStartFixture = Record<string, unknown> | RawMatch | null | undefined;

export function resolveBullionFixtureScheduledStartRaw(fixture: ScheduledStartFixture): string | null {
    if (!fixture || typeof fixture !== "object") return null;
    const m = fixture as Record<string, unknown>;
    const candidates = [
        m.start,
        m["starts-at"],
        m.startTime,
        m.start_time,
        m.scheduledStart,
        m.scheduled_start,
    ];
    for (const raw of candidates) {
        if (raw == null || String(raw).trim() === "") continue;
        if (raw instanceof Date) return raw.toISOString();
        const text = String(raw).trim();
        if (text) return text;
    }
    return null;
}

/** JS `Date` rejects PostgreSQL ISO strings with a 2-digit offset (`+00`) — normalize before parse. */
function normalizeDatetimeStringForParse(raw: unknown): string | Date {
    if (raw instanceof Date) return raw;
    const text = String(raw).trim();
    if (!text) return text;
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?[+-]\d{2}$/.test(text)) {
        return `${text}:00`;
    }
    return text;
}

export function parseBullionFixtureDatetimeToMs(raw: unknown): number | null {
    if (raw == null || raw === "") return null;
    if (raw instanceof Date) {
        const t = raw.getTime();
        return Number.isFinite(t) ? t : null;
    }
    const t = new Date(normalizeDatetimeStringForParse(raw)).getTime();
    return Number.isFinite(t) ? t : null;
}

export function resolveBullionFixtureScheduledStartMs(fixture: ScheduledStartFixture): number | null {
    const raw = resolveBullionFixtureScheduledStartRaw(fixture);
    if (!raw) return null;
    return parseBullionFixtureDatetimeToMs(raw);
}

export function resolveBullionFixtureScheduledStartIso(fixture: ScheduledStartFixture): string | null {
    const ms = resolveBullionFixtureScheduledStartMs(fixture);
    if (ms == null) return null;
    return new Date(ms).toISOString();
}
